import { z } from 'zod';
import {
  ApplicationMappingView,
  ProcessMappingView,
  QueryMetadata,
  type GlobalFilters,
  type QueryResult,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';
import type { WriteOutcome } from './httpIdentityRepository';

/**
 * The application-to-product studio, over HTTP.
 *
 * The read sits on the `Repositories` port; the three commands do not, for the
 * same reason the delegation writes do not — every method on that port returns
 * a read model, and a command is not a query.
 *
 * `refusal` is more careful here than elsewhere. When the database rejects an
 * endpoint pair (RM-003) the route answers with the permitted pairs, and the
 * whole point of that answer is that a person reads it. Flattening it to
 * "Request failed (422)" would turn the clearest demonstration in the product
 * that the model is enforced into a demonstration that the software is broken.
 */

const Envelope = z.object({ data: ApplicationMappingView, meta: QueryMetadata });
const ProcessEnvelope = z.object({ data: ProcessMappingView, meta: QueryMetadata });

export interface RelationshipProposal {
  fromEntityId: string;
  toEntityId: string;
  role?: string;
  allocationPercent?: number | null;
  rationale: string;
  /**
   * Which working set the edge joins. Omitted by the application studio, whose
   * set is the route's default; the process studio names its own.
   */
  mappingSetId?: string;
  /** Omitted by the application studio, whose set holds one relationship type. */
  relationshipType?: string;
}

/** A refusal carrying whatever the server was able to explain about it. */
export type MappingWriteOutcome =
  | { ok: true; id: string }
  | { ok: false; message: string; permitted?: string[] };

async function refusal(response: Response): Promise<MappingWriteOutcome> {
  const body = (await response.json().catch(() => ({}))) as {
    message?: string;
    problems?: string[];
    permitted?: string[];
  };
  const detail = body.problems?.length ? ` ${body.problems.join(' ')}` : '';
  return {
    ok: false,
    message: `${body.message ?? `Request failed (${response.status}).`}${detail}`,
    permitted: body.permitted,
  };
}

export function createHttpMappingRepository(baseUrl: string) {
  return {
    async getProcessMapping(
      filters: GlobalFilters,
      processRootId?: string,
    ): Promise<QueryResult<ProcessMappingView>> {
      const params = filterParams(filters);
      if (processRootId) params.set('process', processRootId);
      const response = await apiFetch(`${baseUrl}/mapping/process-product?${params.toString()}`);
      if (!response.ok) {
        throw new Error(
          `GET /mapping/process-product failed: ${response.status} ${response.statusText}`,
        );
      }
      return ProcessEnvelope.parse((await response.json()) as unknown);
    },

    async getApplicationMapping(
      filters: GlobalFilters,
    ): Promise<QueryResult<ApplicationMappingView>> {
      const params = filterParams(filters);
      const response = await apiFetch(`${baseUrl}/mapping/application-product?${params.toString()}`);
      if (!response.ok) {
        throw new Error(
          `GET /mapping/application-product failed: ${response.status} ${response.statusText}`,
        );
      }
      return Envelope.parse((await response.json()) as unknown);
    },

    async proposeRelationship(draft: RelationshipProposal): Promise<MappingWriteOutcome> {
      const response = await apiFetch(`${baseUrl}/relationships`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (!response.ok) return refusal(response);
      const body = (await response.json()) as { id: string };
      return { ok: true, id: body.id };
    },

    async updateRelationship(
      relationshipId: string,
      change: { role?: string; allocationPercent?: number | null; validTo?: string | null; rationale: string },
    ): Promise<MappingWriteOutcome> {
      const response = await apiFetch(`${baseUrl}/relationships/${relationshipId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(change),
      });
      return response.ok ? { ok: true, id: relationshipId } : refusal(response);
    },

    /** Not "delete". RM-014 refuses that on a published edge, and is right to. */
    async supersedeRelationship(
      relationshipId: string,
      retirement: { reason: string; category?: string },
    ): Promise<MappingWriteOutcome> {
      const response = await apiFetch(`${baseUrl}/relationships/${relationshipId}/supersede`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(retirement),
      });
      return response.ok ? { ok: true, id: relationshipId } : refusal(response);
    },

    /** Puts the working set in front of an approver. It closes to edits until they rule. */
    async submitMappingSet(setId: string, note: string): Promise<MappingWriteOutcome> {
      const response = await apiFetch(`${baseUrl}/mapping-sets/${setId}/submit`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      return response.ok ? { ok: true, id: setId } : refusal(response);
    },

    /** Opens version n+1 as a draft, copying every live edge of the published one. */
    async cloneMappingSet(setId: string, reason: string): Promise<MappingWriteOutcome> {
      const response = await apiFetch(`${baseUrl}/mapping-sets/${setId}/clone`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!response.ok) return refusal(response);
      const body = (await response.json()) as { id: string };
      return { ok: true, id: body.id };
    },

    /** Records a process the catalogue does not have, so the map can reach it. */
    async createProcess(draft: {
      name: string;
      description: string;
      parentProcessId?: string | null;
      criticality?: string;
    }): Promise<MappingWriteOutcome> {
      const response = await apiFetch(`${baseUrl}/processes`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (!response.ok) return refusal(response);
      const body = (await response.json()) as { id: string };
      return { ok: true, id: body.id };
    },
  };
}

export type { WriteOutcome };
